<template>
  <div class="ov-page">
    <div class="ov-header">
      <div>
        <h1 class="ov-header__title">Mon profil</h1>
        <p class="ov-header__subtitle mt-1">Gérez vos informations personnelles et votre photo de profil.</p>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== INFORMATIONS PERSONNELLES ===== -->
      <form class="ov-card" novalidate @submit.prevent="submitForm">
        <div class="ov-card__head">
          <div class="ov-card__title">Informations personnelles</div>
        </div>

        <div class="ov-card__body">
          <div class="ov-row">
            <div class="ov-field">
              <label class="ov-label" for="up-nom">Nom<span class="ov-required">*</span></label>
              <input
                id="up-nom"
                v-model.trim="$v.userUpdating.nom.$model"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': usernameError.length }"
                placeholder="Votre nom"
                autocomplete="family-name"
              />
              <div v-if="usernameError.length" class="ov-error">{{ usernameError[0] }}</div>
            </div>

            <div class="ov-field">
              <label class="ov-label" for="up-prenoms">Prénoms<span class="ov-required">*</span></label>
              <input
                id="up-prenoms"
                v-model.trim="$v.userUpdating.prenoms.$model"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': surnameError.length }"
                placeholder="Vos prénoms"
                autocomplete="given-name"
              />
              <div v-if="surnameError.length" class="ov-error">{{ surnameError[0] }}</div>
            </div>

            <div class="ov-field ov-col-full">
              <label class="ov-label" for="up-login">Pseudo<span class="ov-required">*</span></label>
              <input
                id="up-login"
                v-model.trim="$v.userUpdating.login.$model"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': loginError.length }"
                placeholder="Votre pseudo"
                autocomplete="username"
              />
              <div v-if="loginError.length" class="ov-error">{{ loginError[0] }}</div>
            </div>
          </div>

          <div class="up-readonly">
            <div class="up-readonly__title">Informations du compte</div>
            <div class="ov-stat">
              <span class="ov-stat__label">E-mail</span>
              <span class="ov-stat__value">{{ userUpdatingMail || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Rôle</span>
              <span class="ov-stat__value">{{ userUpdating.roleLibelle || '—' }}</span>
            </div>
            <div class="ov-hint">L'e-mail et le rôle ne sont modifiables que par un administrateur.</div>
          </div>
        </div>

        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="reinitForm()">
            Annuler les modifications
          </button>
          <button type="submit" class="ov-btn ov-btn--primary" :disabled="overlay">
            <v-icon small>mdi-content-save-outline</v-icon>
            Enregistrer
          </button>
        </div>
      </form>

      <!-- ===== PHOTO DE PROFIL ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="up-identity">
            <div class="up-avatar">
              <img v-if="avatarSrc" :src="avatarSrc" alt="Photo de profil" />
              <span v-else>{{ initials }}</span>
            </div>
            <div class="up-identity__name">{{ userUpdating.nom }} {{ userUpdating.prenoms }}</div>
            <span v-if="userUpdating.roleLibelle" class="ov-badge ov-badge--navy ov-badge--plain">{{ userUpdating.roleLibelle }}</span>
          </div>

          <div class="ov-card__body up-photo">
            <div class="ov-label">Photo de profil</div>
            <div
              class="up-drop"
              :class="{ 'up-drop--over': dragOver }"
              role="button"
              tabindex="0"
              @click="$refs.fileInput.click()"
              @keyup.enter="$refs.fileInput.click()"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onPictureDrop"
            >
              <v-icon size="22">mdi-camera-plus-outline</v-icon>
              <span v-if="pictureProfilSelected" class="up-drop__name">{{ pictureProfilSelected.name }}</span>
              <span v-else>Cliquez ou glissez une photo ici</span>
              <small>PNG ou JPG</small>
            </div>
            <input
              ref="fileInput"
              type="file"
              accept="image/jpeg, image/png"
              style="display: none"
              @click="$event.target.value = ''"
              @change="selectPicture($event.target.files[0])"
            />
            <div v-if="pictureError" class="ov-error">{{ pictureError }}</div>

            <button
              type="button"
              class="ov-btn ov-btn--primary ov-btn--block mt-4"
              :disabled="!canSendPicture"
              @click="sendOrUpdatePhotoProfil"
            >
              <v-icon small>mdi-upload</v-icon>
              {{ pictureReading ? 'Lecture du fichier...' : 'Enregistrer la photo' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_GET_DOCUMENT_BASE64,
  API_GET_DOCUMENT_URL,
  API_SEND_DOCUMENTS,
  API_UPDATE_USER,
  HEADERS,
} from "../globalConfig/globalConstConfig";
import { required, minLength } from "vuelidate/lib/validators";
import { showErrorMessage, showSuccessMessage } from "@/modules/messages/messageProcess";
export default {
  name: "UserProfilCompagnieTransport",
  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: null,

      pictureProfilSelected: null,
      pictureProfilBase64: null,
      // Vrai pendant la lecture du fichier : l'envoi reste bloqué jusqu'à la fin
      pictureReading: false,
      pictureError: null,
      dragOver: false,

      userDataUpdating: {
        datas: [],
      },

      userUpdating: {
        id: null,
        nom: null,
        prenoms: null,
        login: null,
        roleCode: null,
        roleLibelle: null,
      },

      userUpdatingMail: null,

      documentToSend: {
        datas: [],
      },

      pictureProfilModel: {
        compagnieTransportRaisonSociale: null,
        login: null,
        typeDocument: null,
        content: null,
        typeMime: null,
      },

      pictureProfilUpdating: {
        id: null,
        compagnieTransportRaisonSociale: null,
        login: null,
        typeDocument: null,
        content: null,
        typeMime: null,
      },

      photoProfilObject: {
        data: {
          login: null,
          typeDocument: null,
        },
      },

      photoProfilUrl: null,
      photoProfilBase64: null,
      photoProfilDocumentId: null,
    };
  },

  validations: {
    userUpdating: {
      nom: {
        required,
        minLength: minLength(2),
      },
      prenoms: {
        required,
        minLength: minLength(2),
      },
      login: {
        required,
        minLength: minLength(2),
      },
    },
  },

  methods: {
    reinitForm() {
      this.userUpdating.id = this.$store.state.userAuthentified.id;
      this.userUpdating.nom = this.$store.state.userAuthentified.nom;
      this.userUpdating.prenoms = this.$store.state.userAuthentified.prenoms;
      this.userUpdating.login = this.$store.state.userAuthentified.login;
      this.userUpdating.roleCode = this.$store.state.userAuthentified.roleCode;
      this.userUpdating.roleLibelle =
        this.$store.state.userAuthentified.roleLibelle;
      this.userUpdatingMail = this.$store.state.userAuthentified.email;
      this.$v.$reset();
    },

    // CHOIX D'UNE PHOTO (CLIC OU GLISSER-DÉPOSER), PNG OU JPG UNIQUEMENT
    selectPicture(file) {
      if (!file) return;
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        this.pictureError = 'La photo doit être une image PNG ou JPG.';
        return;
      }
      this.pictureError = null;
      this.pictureProfilSelected = file;
      this.convertirEnBase64(file);
    },

    onPictureDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      this.selectPicture(file);
    },

    returnTypeMime(file) {
      var formatTypeMime = "";
      if (file.type == "image/png") {
        formatTypeMime = "FORMAT_PNG";
      }
      if (file.type == "image/jpeg") {
        formatTypeMime = "FORMAT_PNG";
      }
      if (file.type == "image/gif") {
        formatTypeMime = "FORMAT_GIF";
      }
      if (file.type == "application/pdf") {
        formatTypeMime = "FORMAT_PDF";
      }
      return formatTypeMime;
    },

    returnDocumentId(roleCode) {
      var id = 0;
      if (roleCode == "RoleUtiGareCompagnieTransport") {
        id = 55;
      }
      if (roleCode == "RoleAdminCompagnieTransport") {
        id = 102;
      }
      if (roleCode == "RoleAdminSocieteMere") {
        id = 52;
      }
      return id;
    },

    // SOUMETTRE FORMULAIRE
    submitForm() {
      this.$v.$touch();
      if (this.$v.userUpdating.$invalid) {
        this.errorMsg = "Certains champs sont requis";
        showErrorMessage();
      } else {
        this.updateUserProfil();
      }
    },

    async sendOrUpdatePhotoProfil() {
      if (!this.pictureProfilSelected) {
        this.errorMsg = "Veuillez sélectionner une photo avant de continuer.";
        showErrorMessage();
        return;
      }
      
      if (this.photoProfilUrl == null || this.photoProfilUrl == "") {
        this.sendProfilPicture();
      } else {
        this.updatePhotoProfil();
      }
    },

    async convertirEnBase64(file) {
      this.pictureReading = true;
      this.pictureProfilModel.content = null;
      this.pictureProfilUpdating.content = null;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        this.pictureReading = false;
      };
      reader.onload = (e) => {
        this.pictureProfilBase64 = e.target.result;
        this.pictureProfilModel.content =
          this.pictureProfilBase64.split(",")[1];
        if (this.photoProfilBase64 != null || this.photoProfilBase64 !== "") {
          this.pictureProfilUpdating.content =
            this.pictureProfilBase64.split(",")[1];
        }
      };
    },

    async updatePhotoProfil() {
      this.overlay = true;
      this.pictureProfilUpdating.id = this.photoProfilDocumentId;
      this.pictureProfilUpdating.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.pictureProfilUpdating.login = this.$store.state.userAuthentified.login;
      this.pictureProfilUpdating.typeDocument = "PHOTO_PROFIL";
      this.pictureProfilUpdating.typeMime = this.returnTypeMime(
        this.pictureProfilSelected
      );
      this.documentToSend.datas.push(this.pictureProfilUpdating);
      await axios
        .put(API_SEND_DOCUMENTS, this.documentToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.getUrlPhotoProfil();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = "Impossible de modifier la photo de profil";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage()
        })
        .finally(() => {
          this.documentToSend.datas = [];
          this.overlay = false;
        });
    },

    async sendProfilPicture() {
      this.overlay = true;
      this.pictureProfilModel.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.pictureProfilModel.login = this.$store.state.userAuthentified.login;
      this.pictureProfilModel.typeDocument = "PHOTO_PROFIL";
      this.pictureProfilModel.typeMime = this.returnTypeMime(
        this.pictureProfilSelected
      );
      this.documentToSend.datas.push(this.pictureProfilModel);
      await axios
        .post(API_SEND_DOCUMENTS, this.documentToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.getUrlPhotoProfil();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = "Impossible de changer la photo de profil";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage()
        })
        .finally(() => {
          this.documentToSend.datas = [];
          this.overlay = false;
        });
    },

    async retrieveUserInfo() {
      /**
       * RECUPERER LES DONNÉES UTILISATEURS
       */
      if (this.$store.state.userAuthentified !== null) {
        this.userUpdating.id = this.$store.state.userAuthentified.id;
        this.userUpdating.nom = this.$store.state.userAuthentified.nom;
        this.userUpdating.prenoms = this.$store.state.userAuthentified.prenoms;
        this.userUpdating.login = this.$store.state.userAuthentified.login;
        this.userUpdating.roleCode =
          this.$store.state.userAuthentified.roleCode;
        this.userUpdating.roleLibelle =
          this.$store.state.userAuthentified.roleLibelle;
        this.userUpdatingMail = this.$store.state.userAuthentified.email;
      }
    },

    //MODIFIER COMPTE UTILISATEUR
    async updateUserProfil() {
      this.overlay = true;
      this.userDataUpdating.datas.push(this.userUpdating);
      await axios
        .put(API_UPDATE_USER, this.userDataUpdating, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              this.$swal.fire(
                "Modification réussie",
                this.successMsg,
                "success"
              );
              this.userDataUpdating.datas = [];
              this.$store.commit("UPDATE_USER_PROFIL", response.data.items[0]);
            } else {
              this.errorMsg = response.data.status.message;
              this.$swal.fire("Opération échouée", this.errorMsg, "error");
              this.userDataUpdating.datas = [];
            }
          } else if (response.status == 204) {
            this.errorMsg = "Erreur lors de la modification du profil";
            showErrorMessage();
            this.userDataUpdating.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.userDataUpdating.datas = [];
          }
        })
        .catch((e) => {
          this.$swal.fire("Opération échouée", String(e), "error");
          this.userDataUpdating.datas = [];
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    // Obtenir la photo de profil de l'utilisateur
    async getUrlPhotoProfil() {
      this.photoProfilObject.data.login = this.$store.state.userAuthentified.login;
      this.photoProfilObject.data.typeDocument = "PHOTO_PROFIL";
      this.loading = true;
      await axios
        .post(API_GET_DOCUMENT_URL, this.photoProfilObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.photoProfilUrl = response.data.item.url;
              this.photoProfilDocumentId = response.data.item.id;
              this.$store.commit(
                "UPDATE_PICTURE_PROFIL",
                response.data.item.url
              );
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getBase64PhotoProfil() {
      this.photoProfilObject.data.login = this.$store.state.userAuthentified.login;
      this.photoProfilObject.data.typeDocument = "PHOTO_PROFIL";
      this.loading = true;
      await axios
        .post(API_GET_DOCUMENT_BASE64, this.photoProfilObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.photoProfilBase64 = response.data.item.content;
              this.pictureProfilBase64 =
                "data:image/png;base64," + response.data.item.content;
              this.$store.commit("UPDATE_PICTURE_PROFIL", this.pictureProfilBase64);
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage("Erreur");
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage("Erreur");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },

  computed: {
    avatarSrc() {
      return this.pictureProfilBase64 || this.$store.state.pictureProfilUser;
    },

    initials() {
      return [this.userUpdating.nom, this.userUpdating.prenoms]
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || '?';
    },

    canSendPicture() {
      return !!this.pictureProfilSelected && !this.pictureReading && !!this.pictureProfilModel.content && !this.overlay;
    },

    usernameError() {
      const errors = [];
      if (!this.$v.userUpdating.nom.$dirty) return errors;
      !this.$v.userUpdating.nom.required &&
        errors.push("Le nom de l'utilsateur est obligatoire.");
      !this.$v.userUpdating.nom.minLength &&
        errors.push("Au moins 2 caractères.");
      return errors;
    },

    surnameError() {
      const errors = [];
      if (!this.$v.userUpdating.prenoms.$dirty) return errors;
      !this.$v.userUpdating.prenoms.required &&
        errors.push("Le prenom de l'utilsateur est obligatoire.");
      !this.$v.userUpdating.prenoms.minLength &&
        errors.push("Au moins 2 caractères.");
      return errors;
    },

    loginError() {
      const errors = [];
      if (!this.$v.userUpdating.login.$dirty) return errors;
      !this.$v.userUpdating.login.required &&
        errors.push("Le login de l'utilsateur est obligatoire.");
      !this.$v.userUpdating.login.minLength &&
        errors.push("Au moins 2 caractères.");
      return errors;
    },
  },

  mounted() {
    this.getBase64PhotoProfil();
    this.getUrlPhotoProfil();
    this.retrieveUserInfo();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.up-readonly {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid $ov-line;
}

.up-readonly__title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 800;
  color: $ov-navy;
}

.up-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 22px 20px;
  text-align: center;
  border-bottom: 1px solid $ov-line;
}

.up-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  margin-bottom: 6px;
  overflow: hidden;
  border-radius: 50%;
  background: linear-gradient(135deg, $ov-green, $ov-navy);
  box-shadow: 0 0 0 4px #ffffff, 0 0 0 5px $ov-line;
  color: #ffffff;
  font-size: 36px;
  font-weight: 900;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.up-identity__name {
  font-size: 17px;
  font-weight: 800;
  color: $ov-navy;
}

.up-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: $ov-muted;
  border: 1px dashed rgba(15, 23, 42, 0.2);
  border-radius: 12px;
  background: $ov-soft;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  small {
    font-size: 12px;
    font-weight: 500;
    color: $ov-subtle;
  }

  .v-icon {
    color: $ov-green !important;
  }

  &:hover,
  &:focus-visible,
  &--over {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.05);
  }
}

.up-drop__name {
  max-width: 100%;
  font-weight: 700;
  color: $ov-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
